import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faSearch,
  faBan,
  faCheck,
  faSync,
  faToggleOn,
  faToggleOff
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SuccessModal from '../../components/SuccessModal';

const CatiAgencyBlacklist = () => {
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [settings, setSettings] = useState({ blacklist_system_active: true });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchBlacklist();
    fetchSettings();
  }, [currentPage, searchTerm]);

  const fetchBlacklist = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        per_page: 15
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await api.get(`/cati/blacklist?${params}`);
      
      if (response.data.success) {
        const data = response.data.data;
        setBlacklist(data.data || []);
        setPagination({
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total
        });
      }
    } catch (error) {
      console.error('Error fetching blacklist:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/cati/blacklist/settings');
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleToggleSystem = async () => {
    try {
      setLoadingSettings(true);
      const newValue = !settings.blacklist_system_active;
      const response = await api.put('/cati/blacklist/settings', {
        blacklist_system_active: newValue
      });

      if (response.data.success) {
        setSettings(prev => ({ ...prev, blacklist_system_active: newValue }));
        setSuccessMessage(`Système de blacklist ${newValue ? 'activé' : 'désactivé'}`);
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error updating settings:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleAction = async (id, action) => {
    try {
      const response = await api.put(`/cati/blacklist/${id}`, { action });
      
      if (response.data.success) {
        setSuccessMessage('Compteur réinitialisé');
        setShowSuccessModal(true);
        fetchBlacklist();
      }
    } catch (error) {
      console.error('Error updating agency:', error);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">BLACKLIST DES AGENCES CATI</h1>
                <p className="text-gray-600">Gérer les agences blacklistées (3 "OK" = blacklist automatique)</p>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center cursor-pointer">
                  <span className="mr-3 text-sm font-medium text-gray-700">
                    Système de blacklist
                  </span>
                  <button
                    onClick={handleToggleSystem}
                    disabled={loadingSettings}
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    style={{
                      backgroundColor: settings.blacklist_system_active ? '#dc2626' : '#9ca3af'
                    }}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.blacklist_system_active ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="ml-2 text-sm text-gray-600">
                    {settings.blacklist_system_active ? 'Activé' : 'Désactivé'}
                  </span>
                </label>
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par code agence..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin mb-4" />
                <p className="text-gray-600">Chargement...</p>
              </div>
            ) : blacklist.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-600 text-lg">Aucune agence dans la blacklist</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Code Agence</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Compteur OK</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {blacklist.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">{item.code_agence}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">{item.ok_count}/3</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {item.is_blacklisted ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <FontAwesomeIcon icon={faBan} className="mr-1" />
                                Blacklisté
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                Actif
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleAction(item.id, 'reset_count')}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <FontAwesomeIcon icon={faSync} className="mr-1" />
                              Réinitialiser
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.last_page > 1 && (
                  <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="text-sm text-gray-700 text-center sm:text-left w-full sm:w-auto">
                        Page {pagination.current_page} sur {pagination.last_page}
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto justify-center sm:justify-end">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="flex-1 sm:flex-initial px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Précédent
                        </button>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === pagination.last_page}
                          className="flex-1 sm:flex-initial px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Suivant
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />
    </div>
  );
};

export default CatiAgencyBlacklist;

